import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import api from "../utils/axiosConfig";
import SideBar from "./SideBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faUserPlus,
  faUser,
  faEnvelope,
  faLock,
  faMapMarkerAlt,
  faUserTag,
} from "@fortawesome/free-solid-svg-icons";
import "../styles/register.css";

const API_URL = process.env.REACT_APP_API_URL;

const SignUp = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [userName, setUserName] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [roles, setRoles] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const navigate = useNavigate();

  const handleRoleChange = (selectedRoles) => {
    setRoles(selectedRoles ? selectedRoles.map((role) => role.value) : []);
  };

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const response = await api.post(`${API_URL}/auth/signup`, {
        userName,
        email,
        password,
        roles,
        location: selectedState,
      });
      setMessage({ type: "success", text: "User registered successfully!" });
      // Reset form
      setUserName("");
      setEmail("");
      setPassword("");
      setSelectedState("");
      setRoles([]);
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Registration failed. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const roleOptions = [
    { value: "admin", label: "Admin" },
    { value: "user", label: "User" },
    { value: "hr", label: "HR" },
  ];

  // Custom styles for react-select to match Akar theme
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      border: state.isFocused ? "2px solid #CE452B" : "1px solid #e0e0e0",
      borderRadius: "10px",
      padding: "4px 8px",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(206, 69, 43, 0.1)" : "none",
      cursor: "pointer",
      minHeight: "48px",
      "&:hover": {
        borderColor: "#CE452B",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#CE452B"
        : state.isFocused
        ? "rgba(206, 69, 43, 0.1)"
        : "white",
      color: state.isSelected ? "white" : "#333",
      cursor: "pointer",
      "&:active": {
        backgroundColor: "#CE452B",
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "rgba(206, 69, 43, 0.1)",
      borderRadius: "6px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#CE452B",
      fontWeight: "500",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#CE452B",
      "&:hover": {
        backgroundColor: "#CE452B",
        color: "white",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#999",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? "#CE452B" : "#999",
      "&:hover": {
        color: "#CE452B",
      },
    }),
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-container">
        <div className="register-page">
          {/* Header */}
          <div className="register-header">
            <div className="register-icon">
              <FontAwesomeIcon icon={faUserPlus} />
            </div>
            <h1 className="register-title">Register New User</h1>
            <p className="register-subtitle">
              Create a new account with role-based access
            </p>
          </div>

          {/* Registration Card */}
          <div className="register-card">
            {/* Message Alert */}
            {message.text && (
              <div className={`alert alert-${message.type}`}>
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit} className="register-form">
              {/* Name Field */}
              <div className="form-group">
                <label htmlFor="userName">
                  <FontAwesomeIcon icon={faUser} className="label-icon" />
                  Full Name
                </label>
                <input
                  type="text"
                  id="userName"
                  placeholder="Enter full name"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  required
                />
              </div>

              {/* Email Field */}
              <div className="form-group">
                <label htmlFor="email">
                  <FontAwesomeIcon icon={faEnvelope} className="label-icon" />
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  placeholder="Enter email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              {/* Password Field */}
              <div className="form-group">
                <label htmlFor="password">
                  <FontAwesomeIcon icon={faLock} className="label-icon" />
                  Password
                </label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <span
                    className="password-toggle"
                    onClick={toggleShowPassword}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} />
                  </span>
                </div>
              </div>

              {/* Roles Multi-Select */}
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faUserTag} className="label-icon" />
                  Assign Roles
                </label>
                <Select
                  isMulti
                  options={roleOptions}
                  value={roleOptions.filter((role) => roles.includes(role.value))}
                  onChange={handleRoleChange}
                  styles={selectStyles}
                  placeholder="Select roles..."
                  className="role-select"
                />
              </div>

              {/* Submit Button */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="btn-spinner"></span>
                      Registering...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faUserPlus} style={{ marginRight: "8px" }} />
                      Register User
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignUp;